export const ROUTE_KIND = {
  standard: { label: 'Ruta completa', description: 'Etapas progresivas con formación estructurada' },
  fast_track: { label: 'Ruta rápida', description: 'Lista priorizada de certificaciones clave' },
};

export const GROWTH_STORAGE_KEY = 'growthPlannerData';
export const GROWTH_BACKUP_KEY = 'growthPlannerData_backup';
export const GROWTH_LOCAL_BACKUP_KEY = 'growthPlannerData_local_backup';

export const ROUTE_STATUS = {
  active: { label: 'Activa', color: 'success' },
  paused: { label: 'Pausada', color: 'warning' },
  completed: { label: 'Completada', color: 'info' },
  archived: { label: 'Archivada', color: 'default' },
};

export const PRIORITY = {
  low: { label: 'Baja', color: 'default' },
  medium: { label: 'Media', color: 'primary' },
  high: { label: 'Alta', color: 'error' },
};

export const ITEM_STATUS = {
  pending: { label: 'Pendiente', color: 'default' },
  in_progress: { label: 'En progreso', color: 'primary' },
  completed: { label: 'Completado', color: 'success' },
  paused: { label: 'Pausado', color: 'warning' },
};

export const ITEM_TYPES = {
  course: { label: 'Curso', icon: '📚' },
  certification: { label: 'Certificación', icon: '🏆' },
  specialization: { label: 'Especialización', icon: '🎓' },
  book: { label: 'Libro', icon: '📖' },
  project: { label: 'Proyecto', icon: '💼' },
  practice: { label: 'Práctica', icon: '🔧' },
};

export const PROJECT_STATUS = {
  planning: { label: 'Planificación', color: 'default' },
  in_progress: { label: 'En progreso', color: 'primary' },
  completed: { label: 'Completado', color: 'success' },
};

const uid = () => `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

export const createLearningItem = (overrides = {}) => ({
  id: uid(),
  type: 'course',
  title: '',
  institution: '',
  url: '',
  estimatedHours: 0,
  status: 'pending',
  progress: 0,
  startDate: '',
  targetDate: '',
  notes: '',
  skills: [],
  ...overrides,
});

/** @deprecated use createLearningResource + createPlacement */
export const createLegacyLearningItem = createLearningItem;

export const createStage = (overrides = {}) => ({
  id: uid(),
  title: '',
  description: '',
  progress: 0,
  deadline: '',
  priority: 'medium',
  expectedOutcomes: '',
  items: [],
  ...overrides,
});

export const createRoute = (overrides = {}) => ({
  id: uid(),
  name: '',
  description: '',
  objective: '',
  targetRole: '',
  startDate: new Date().toISOString().slice(0, 10),
  targetDate: '',
  status: 'active',
  priority: 'medium',
  progress: 0,
  estimatedHours: 0,
  investedHours: 0,
  notes: '',
  stages: [],
  projectIds: [],
  profileId: null,
  routeKind: 'standard',
  createdAt: new Date().toISOString(),
  ...overrides,
});

export const createSkill = (overrides = {}) => ({
  id: uid(),
  name: '',
  currentLevel: 1,
  targetLevel: 3,
  progress: 0,
  relatedItemIds: [],
  relatedProjectIds: [],
  ...overrides,
});

export const createProject = (overrides = {}) => ({
  id: uid(),
  name: '',
  description: '',
  techStack: [],
  demonstratedSkills: [],
  links: [],
  status: 'planning',
  documentation: '',
  routeIds: [],
  ...overrides,
});

export const createWeeklyGoal = (overrides = {}) => ({
  id: uid(),
  title: '',
  targetHours: 5,
  completedHours: 0,
  weekStart: getWeekStart(),
  ...overrides,
});

export const createMonthlyGoal = (overrides = {}) => ({
  id: uid(),
  title: '',
  targetHours: 20,
  completedHours: 0,
  month: new Date().toISOString().slice(0, 7),
  ...overrides,
});

export const createStudyLogEntry = (overrides = {}) => ({
  id: uid(),
  date: new Date().toISOString().slice(0, 10),
  hours: 1,
  routeId: null,
  itemId: null,
  notes: '',
  ...overrides,
});

export function getWeekStart(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return d.toISOString().slice(0, 10);
}

export const initialGrowthData = {
  routes: [],
  learningResources: [],
  skills: [],
  projects: [],
  focus: {
    weeklyGoals: [],
    monthlyGoals: [],
    studyLog: [],
    streak: { current: 0, longest: 0, lastStudyDate: null },
  },
};

export function computeItemProgress(item) {
  if (!item) return 0;
  if (item.status === 'completed') return 100;
  return Math.min(100, Math.max(0, item.progress || 0));
}

export function computeStageProgress(stage, learningResources = []) {
  const items = stage?.items || [];
  if (!items.length) return stage?.progress || 0;

  const map = new Map((learningResources || []).map((r) => [r.id, r]));
  const resolved = items.map((p) => {
    if (p.resourceId) {
      const res = map.get(p.resourceId);
      return res ? { ...res, targetDate: p.targetDate } : p;
    }
    return p;
  });

  const total = resolved.reduce((sum, item) => sum + computeItemProgress(item), 0);
  return Math.round(total / resolved.length);
}

export function computeRouteProgress(route, learningResources = []) {
  if (!route.stages?.length) return route.progress || 0;
  const total = route.stages.reduce(
    (sum, stage) => sum + computeStageProgress(stage, learningResources),
    0
  );
  return Math.round(total / route.stages.length);
}

export function computeSkillProgress(skill) {
  if (skill.targetLevel <= skill.currentLevel) return 100;
  const range = skill.targetLevel - skill.currentLevel;
  const current = (skill.progress / 100) * range + skill.currentLevel;
  return Math.round(((current - skill.currentLevel) / range) * 100);
}

export function computeStreak(studyLog) {
  if (!studyLog?.length) return { current: 0, longest: 0, lastStudyDate: null };

  const uniqueDates = [...new Set(studyLog.map((e) => e.date))].sort().reverse();
  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);

  let current = 0;
  if (uniqueDates[0] === today || uniqueDates[0] === yesterday) {
    let checkDate = new Date(uniqueDates[0]);
    for (const dateStr of uniqueDates) {
      if (dateStr === checkDate.toISOString().slice(0, 10)) {
        current++;
        checkDate.setDate(checkDate.getDate() - 1);
      } else break;
    }
  }

  let longest = 0;
  let streak = 0;
  const sorted = [...uniqueDates].sort();
  for (let i = 0; i < sorted.length; i++) {
    if (i === 0) {
      streak = 1;
    } else {
      const prev = new Date(sorted[i - 1]);
      const curr = new Date(sorted[i]);
      const diff = (curr - prev) / 86400000;
      streak = diff === 1 ? streak + 1 : 1;
    }
    longest = Math.max(longest, streak);
  }

  return { current, longest, lastStudyDate: uniqueDates[0] || null };
}

export function computeConsistencyScore(studyLog, days = 30) {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  const cutoffStr = cutoff.toISOString().slice(0, 10);
  const recentDates = new Set(
    studyLog.filter((e) => e.date >= cutoffStr).map((e) => e.date)
  );
  return Math.round((recentDates.size / days) * 100);
}

export function computeFocusScore(weeklyGoals) {
  const currentWeek = getWeekStart();
  const goals = weeklyGoals.filter((g) => g.weekStart === currentWeek);
  if (!goals.length) return 0;
  const total = goals.reduce((sum, g) => sum + (g.targetHours || 0), 0);
  const done = goals.reduce((sum, g) => sum + Math.min(g.completedHours || 0, g.targetHours || 0), 0);
  if (total === 0) return 0;
  return Math.round((done / total) * 100);
}

export function getTotalStudyHours(studyLog, since = null) {
  return studyLog
    .filter((e) => !since || e.date >= since)
    .reduce((sum, e) => sum + (e.hours || 0), 0);
}

export function getUpcomingDeadlines(routes, learningResources = [], limit = 5) {
  const map = new Map((learningResources || []).map((r) => [r.id, r]));
  const items = [];
  routes.forEach((route) => {
    if (route.targetDate) {
      items.push({ type: 'route', title: route.name, date: route.targetDate, routeId: route.id });
    }
    route.stages?.forEach((stage) => {
      if (stage.deadline) {
        items.push({ type: 'stage', title: `${route.name} → ${stage.title}`, date: stage.deadline, routeId: route.id });
      }
      stage.items?.forEach((placement) => {
        const resource = placement.resourceId ? map.get(placement.resourceId) : placement;
        const targetDate = placement.targetDate || resource?.targetDate;
        const status = resource?.status ?? placement.status;
        const title = resource?.title || placement.title;
        if (targetDate && status !== 'completed') {
          items.push({
            type: 'item',
            title,
            date: targetDate,
            routeId: route.id,
            itemId: placement.resourceId || placement.id,
          });
        }
      });
    });
  });
  return items
    .filter((i) => i.date >= new Date().toISOString().slice(0, 10))
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, limit);
}
